import React, { useState } from 'react'
import {
  Box,
  TextField,
  FormHelperText,
  Button,
  Grid,
  CircularProgress,
  Typography,
  Autocomplete,
  FormLabel,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import axios from 'axios'
import { useSnackbar } from 'notistack'
import UploadIcon from '@mui/icons-material/Upload'
import { useNavigate } from 'react-router-dom'
import { workshop } from './workshop'
import { pattern } from './pattern'
import { usePostApplication } from '../../../api/rh-warranty'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

const quantity = ['1', '2', '3', '4']
export const ApplicationForm = () => {
  const { register, handleSubmit, control, setValue } = useForm({
    mode: 'onChange',
  })

  const { mutateAsync, isLoading } = usePostApplication()
  const { enqueueSnackbar } = useSnackbar()

  const navigate = useNavigate()

  const [submitIsLoading, setSubmitIsLoading] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const onSubmit = async (data) => {
    setSubmitIsLoading(true)
    const file = data?.invoice?.[0]

    const formData = new FormData()
    formData.append('htperformance', file, file?.name ?? '')

    const uploadRes = await axios({
      url: `/api/upload/htperformance-invoice`,
      method: 'POST',
      data: formData,
    })
    const { filename, uploadPath } = uploadRes.data
    if (!filename || !uploadPath) {
      enqueueSnackbar('Error uploading file, please try again', {
        variant: 'error',
      })
      setSubmitIsLoading(false)
      return
    }
    const res = await mutateAsync({
      ...data,
      invoiceImg: uploadRes.data,
    }).catch((err) => {
      enqueueSnackbar(err?.response?.data?.message ?? 'Submit fail', {
        variant: 'error',
      })
    })
    if (res?.status === 200) {
      enqueueSnackbar('Warranty Application sent.', {
        variant: 'success',
      })
      navigate('/')
    }
    setSubmitIsLoading(false)
    return res
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <FormLabel>Name / Nama / 名字</FormLabel>
          <Controller
            name='name'
            control={control}
            defaultValue=''
            rules={{
              required: 'Required',
              maxLength: { value: 35, message: 'Exceed word count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                variant='outlined'
                value={value}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel>Phone Number / Telefon Bimbit / 电话号码</FormLabel>
          <Controller
            name='phone'
            control={control}
            defaultValue=''
            rules={{
              required: 'Required',
              pattern: {
                value: /^(601)[0-46-9]-*[0-9]{7,8}$/,
                message: `Invalid phone number. Phone number should follow the format of 601xxxxxxxx. '+' sign is not required.`,
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                variant='outlined'
                placeholder='Eg. 60123456789'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel>E-mail / E-mel / 电子邮件</FormLabel>
          <Controller
            name='email'
            control={control}
            defaultValue=''
            rules={{
              required: 'Required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
              maxLength: { value: 50, message: 'Exceed word count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                variant='outlined'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel>Tyre Pattern / Corak Tyre / 轮胎类型</FormLabel>
          <Controller
            name='pattern'
            control={control}
            rules={{
              required: 'Required',
            }}
            render={({ field: { onChange }, fieldState: { error } }) => (
              <Autocomplete
                options={pattern}
                onChange={(e, data) => onChange(data)}
                defaultValue={''}
                renderInput={(params) => (
                  <TextField
                    variant='outlined'
                    error={!!error}
                    fullWidth
                    helperText={error ? error.message : null}
                    {...params}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel>Tyre Size / Tayar Size / 轮胎尺寸</FormLabel>
          <Controller
            name='size'
            control={control}
            defaultValue=''
            rules={{
              required: 'Required',
              maxLength: { value: 50, message: 'Exceed word count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                placeholder='Eg. 255/60'
                variant='outlined'
                value={value}
                onChange={onChange}
                error={!!error}
                fullWidth
                helperText={error ? error.message : null}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel>Car Model / Kereta Model / 汽车款式</FormLabel>
          <Controller
            name='carModel'
            control={control}
            defaultValue=''
            rules={{
              required: 'Required',
              maxLength: { value: 50, message: 'Exceed word count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                variant='outlined'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel>Car Plate Number / Nombor Kereta / 车牌号码</FormLabel>
          <Controller
            name='carPlate'
            control={control}
            defaultValue=''
            rules={{
              required: 'Required',
              maxLength: { value: 12, message: 'Exceed character count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                variant='outlined'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel>Tyre Quantity / Kuantiti Tayar / 轮胎数量</FormLabel>
          <Controller
            name='quantity'
            control={control}
            rules={{
              required: 'Required',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                options={quantity}
                onChange={(e, data) => onChange(data)}
                value={value ?? ''}
                autoHighlight={true}
                renderInput={(params) => (
                  <TextField
                    variant='outlined'
                    error={!!error}
                    fullWidth
                    helperText={error ? error.message : null}
                    {...params}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel>Workshop Name / Nama Bengkel / 车厂名称</FormLabel>
          <Controller
            name='workshop'
            control={control}
            rules={{
              required: 'Required',
            }}
            render={({ field: { onChange }, fieldState: { error } }) => (
              <Autocomplete
                options={workshop}
                onChange={(e, data) => onChange(data)}
                defaultValue={''}
                autoHighlight={true}
                renderInput={(params) => (
                  <TextField
                    variant='outlined'
                    error={!!error}
                    fullWidth
                    helperText={error ? error.message : null}
                    {...params}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel>Invoice Number / Nombor Invois / 发票号码</FormLabel>
          <Controller
            name='invoiceNum'
            control={control}
            defaultValue=''
            rules={{
              required: 'Required',
              maxLength: { value: 50, message: 'Exceed word count' },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                variant='outlined'
                value={value}
                fullWidth
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel>Invoice Date / Tarikh Invois / 发票日期</FormLabel>
          <Controller
            name='invoiceDate'
            control={control}
            defaultValue={Date.now()}
            rules={{
              required: 'Required',
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={value}
                  onChange={onChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      error={!!error}
                      fullWidth
                      helperText={error ? error.message : null}
                    />
                  )}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>
        <Grid item xs={12} display='flex' flexDirection='column' justifyContent='center'>
          <FormLabel>Invoice</FormLabel>
          <Box>
            <Controller
              name='invoice'
              control={control}
              defaultValue=''
              rules={{
                required: 'Required',
              }}
              render={({ field: { value }, fieldState: { error } }) => {
                const props = register('invoice')
                const combinedOnchange = (e) => {
                  const fileSize = e?.target?.files?.[0]?.size
                  const fileMb = fileSize / 1024 ** 2
                  if (fileMb > 5) {
                    setUploadError('File size must be within 5MB.')
                    setValue('invoice', null)
                  } else {
                    props.onChange(e)
                    setUploadError('')
                  }
                }
                return (
                  <>
                    <Button variant='outlined' startIcon={<UploadIcon />} component='label'>
                      Upload Proof of Purchase / Muat Naik Bukti Pembelian / 上传购买证明
                      <input
                        type='file'
                        accept='image/*'
                        hidden
                        {...props}
                        onChange={combinedOnchange}
                      />
                    </Button>
                    {error ? <FormHelperText error={true}>{error?.message}</FormHelperText> : null}
                    {value ? <FormHelperText>{value[0]?.name}</FormHelperText> : null}
                    {/* throw error when file size is >2MB */}
                    {uploadError ? (
                      <FormHelperText error={true}>{uploadError}</FormHelperText>
                    ) : null}
                  </>
                )
              }}
            />
          </Box>
        </Grid>
      </Grid>
      <Box my={6} display={'flex'} flexDirection={'column'} gap={1}>
        <Typography variant='caption'>
          *Please make sure to submit your proof of purchase within 2 weeks. Any submission after
          the period of time mentioned are seen as rejection.
        </Typography>
        <Typography variant='caption'>
          *Do make sure the proof of purchase is up-to date and printed invoice. On-screen invoice
          or any other format are not accepted as proof of purchase.
        </Typography>
        <Typography variant='caption'>
          *The Nexen Tire purchased must be the dealership under HT Tyrehub Sdn Bhd. If the provided
          invoice is purchased from other dealership, it will be considered ineligible for the
          warranty campaign.
        </Typography>
        <Typography variant='caption'>
          *By submitting this form, you confirm that you have read, understood, and will respect the{' '}
          <a
            href={'/client-static/roadHazardWarranty/TermsCondition.pdf'}
            target='_blank'
            rel='noopener noreferrer'
          >
            Terms & Conditions
          </a>{' '}
          and{' '}
          <a
            style={{ color: '#002984' }}
            href={'/client-static/tngCampaign/PersonalDataPrivacyNotice.pdf'}
            target='_blank'
            rel='noopener noreferrer'
          >
            Privacy Policy
          </a>
          .
        </Typography>
      </Box>
      <Box mt={5} display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          color='primary'
          type='submit'
          size='large'
          style={{ width: '100%' }}
          disabled={isLoading || submitIsLoading}
        >
          {(isLoading || submitIsLoading) && (
            <CircularProgress style={{ color: '#fff' }} size='1.5rem' />
          )}
          Submit
        </Button>
      </Box>
    </form>
  )
}
